import axios from "axios";
import apiClient from "./v5Api";
import { ENV } from "../configs/environment";
import { normalizeMobilityReport } from "../utlis/normalizeMobilityReport";

export const MOBILITY_REPORT_MEDIA_TYPE =
  "application/vnd.seniorlife.mobility-report.v2+json";
import { SUPPORTED_MOBILITY_SECTIONS } from "../utlis/mobilityReportSections";
export { SUPPORTED_MOBILITY_SECTIONS } from "../utlis/mobilityReportSections";

const modes = [
  "Safety",
  "home_care",
  "senior_living",
  "pharmacy",
  "rehab",
  "Longevity",
];
const publicApi = axios.create({
  baseURL: ENV.API_URL,
  headers: { Accept: MOBILITY_REPORT_MEDIA_TYPE },
});
const object = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const identifier = (value) =>
  (typeof value === "string" && value.trim() !== "") ||
  (typeof value === "number" && Number.isFinite(value));
const validRevision = (value) =>
  identifier(value) &&
  (typeof value !== "number" || (Number.isInteger(value) && value >= 0));
const unsupported = () => {
  const error = new Error(
    "The server returned an unsupported or incomplete mobility report.",
  );
  error.code = "UnsupportedMobilityReport";
  return error;
};

const collectionIds = (collection) =>
  new Set(
    Array.isArray(collection)
      ? collection.map((item) => String(item?.id))
      : Object.entries(collection || {}).map(([key, item]) =>
          String(item?.id ?? key),
        ),
  );
const hasValidReferences = (value, registries) => {
  if (Array.isArray(value))
    return value.every((entry) => hasValidReferences(entry, registries));
  if (!object(value) || value.visible === false) return true;
  return Object.entries(value).every(([key, entry]) => {
    const registryName = {
      metric_ids: "metrics",
      print_metric_ids: "metrics",
      metric_order: "metrics",
      driving_metric_ids: "metrics",
      chart_ids: "charts",
      domain_ids: "domains",
      group_ids: "groups",
      metric_id: "metrics",
      domain_id: "domains",
      group_id: "groups",
    }[key];
    if (registryName && (key.endsWith("_ids") || key === "metric_order")) {
      return (
        Array.isArray(entry) &&
        entry.every(
          (id) => identifier(id) && registries[registryName].has(String(id)),
        )
      );
    }
    if (registryName && entry != null)
      return identifier(entry) && registries[registryName].has(String(entry));
    return hasValidReferences(entry, registries);
  });
};

/** Only unwrap documented DTO envelopes. Raw measurements are never adapted. */
export const unwrapMobilityReport = (
  response,
  { publicSnapshot = false } = {},
) => {
  let document = response;
  if (
    object(document) &&
    !document.schema_version &&
    Object.prototype.hasOwnProperty.call(document, "data")
  ) {
    document = document.data;
  }
  if (
    object(document) &&
    !document.schema_version &&
    Object.prototype.hasOwnProperty.call(document, "data")
  ) {
    document = document.data;
  }
  if (
    object(document) &&
    !document.schema_version &&
    object(document.document)
  ) {
    document = document.document;
  }
  if (
    object(document) &&
    !document.schema_version &&
    document.report?.schema_version === "mobility-report.v2"
  ) {
    document = document.report;
  }
  document = normalizeMobilityReport(document);
  const requiredObjects = [
    "model",
    "context",
    "person",
    "assessment",
    "capabilities",
    "presentation",
    "freshness",
    "metrics",
  ];
  const requiredCollections = ["charts", "explanations"];
  if (
    !object(document) ||
    document.schema_version !== "mobility-report.v2" ||
    !identifier(document.report_id) ||
    !validRevision(document.revision) ||
    requiredObjects.some((key) => !object(document[key])) ||
    (!identifier(document.context.senior_id) &&
      !(
        publicSnapshot &&
        document.presentation.public === true &&
        document.context.senior_id === null
      )) ||
    !modes.includes(document.context.mode) ||
    typeof document.context.content_locale !== "string" ||
    !document.context.content_locale.trim() ||
    !Array.isArray(document.presentation.sections) ||
    document.presentation.sections.length === 0 ||
    document.presentation.sections.some((section) => {
      if (object(section) && section.visible === false) return false;
      const type = typeof section === "string" ? section : section?.type;
      return (
        !SUPPORTED_MOBILITY_SECTIONS.includes(type) ||
        (!object(document[type]) && !Array.isArray(document[type]))
      );
    }) ||
    Object.values(document.metrics).some((metric) => !object(metric)) ||
    requiredCollections.some(
      (key) => !object(document[key]) && !Array.isArray(document[key]),
    ) ||
    (!Array.isArray(document.domains) &&
      !object(document.domains) &&
      !Array.isArray(document.groups) &&
      !object(document.groups)) ||
    (document.context.mode === "rehab" && !object(document.fim)) ||
    (document.context.mode === "Longevity" && !object(document.longevity))
  ) {
    throw unsupported();
  }
  if (
    !hasValidReferences(document, {
      metrics: new Set(Object.keys(document.metrics)),
      domains: collectionIds(document.domains),
      groups: collectionIds(document.groups),
      charts: collectionIds(document.charts),
    })
  )
    throw unsupported();
  return document;
};

const basePath = (seniorId) => {
  if (!identifier(seniorId)) throw new Error("A senior ID is required.");
  return `/v6/seniors/${encodeURIComponent(seniorId)}/report`;
};
const etagOf = (response) =>
  response.headers?.get?.("etag") ??
  response.headers?.etag ??
  response.headers?.ETag ??
  null;
const reportResult = (response, seniorId, options) => {
  const document = unwrapMobilityReport(response, options);
  if (
    seniorId != null &&
    String(document.context.senior_id) !== String(seniorId)
  )
    throw unsupported();
  return { document, etag: etagOf(response) };
};
const headers = () => ({ Accept: MOBILITY_REPORT_MEDIA_TYPE });
const processingError = () => {
  const error = new Error(
    "The report is still being prepared. Please try again shortly.",
  );
  error.code = "MobilityReportProcessing";
  return error;
};
const canceledError = () => {
  const error = new Error("The report request was canceled.");
  error.code = "ERR_CANCELED";
  error.name = "CanceledError";
  return error;
};
const pollDelay = (response) => {
  const retryAfter =
    response.headers?.get?.("retry-after") ??
    response.headers?.["retry-after"] ??
    response.headers?.["Retry-After"];
  const seconds = Number(retryAfter);
  const milliseconds =
    retryAfter == null
      ? 1000
      : Number.isFinite(seconds)
        ? seconds * 1000
        : Date.parse(retryAfter) - Date.now();
  return Math.min(
    5000,
    Math.max(1000, Number.isFinite(milliseconds) ? milliseconds : 1000),
  );
};
const waitForPoll = (delay, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(canceledError());
      return;
    }
    const finish = () => {
      signal?.removeEventListener("abort", cancel);
      resolve();
    };
    const timer = setTimeout(finish, delay);
    const cancel = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", cancel);
      reject(canceledError());
    };
    signal?.addEventListener("abort", cancel, { once: true });
  });
// Polling URLs can never send the staff token to a different server. A 202
// without a supported status URL stays an explicit processing state.
const completedResponse = async (
  initialResponse,
  signal,
  client = apiClient,
) => {
  let response = initialResponse;
  for (let attempt = 0; response.status === 202; attempt += 1) {
    if (signal?.aborted) throw canceledError();
    if (attempt >= 30) throw processingError();
    const body = response.data?.data ?? response.data;
    let url;
    try {
      if (typeof body?.status_url !== "string" || !body.status_url.trim())
        throw processingError();
      const apiBase = new URL(ENV.API_URL, window.location.origin);
      url = new URL(body.status_url, apiBase);
      if (
        !["http:", "https:"].includes(url.protocol) ||
        url.origin !== apiBase.origin ||
        url.username ||
        url.password ||
        url.hash
      )
        throw processingError();
    } catch {
      throw processingError();
    }
    await waitForPoll(pollDelay(response), signal);
    response = await client.get(url.href, { headers: headers(), signal });
  }
  if (signal?.aborted) throw canceledError();
  return response;
};
const requestKey = () =>
  globalThis.crypto?.randomUUID?.() ||
  `mobility-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
const allowed = (value) => value === true || value?.allowed === true;

export const fetchMobilityReport = async (
  seniorId,
  { reportId, revision, activityId, signal, locale } = {},
) => {
  const exact = reportId != null && reportId !== "latest";
  if (exact && !validRevision(revision))
    throw new Error("An exact report revision is required.");
  const response = await apiClient.get(
    `${basePath(seniorId)}${exact ? `/${encodeURIComponent(reportId)}` : ""}`,
    {
      headers: {
        ...headers(),
        ...(!exact && locale ? { "Accept-Language": locale } : {}),
      },
      params: exact
        ? { revision }
        : activityId
          ? { activity_id: activityId }
          : {},
      signal,
    },
  );
  const result = reportResult(
    await completedResponse(response, signal),
    seniorId,
  );
  if (
    exact &&
    (String(result.document.report_id) !== String(reportId) ||
      String(result.document.revision) !== String(revision))
  )
    throw unsupported();
  return result;
};

/** Writes target the server-issued assessment, never a date or latest module. */
export const patchMobilityReport = async (
  seniorId,
  document,
  etag,
  change,
  { signal, idempotencyKey } = {},
) => {
  unwrapMobilityReport(document);
  if (String(document.context.senior_id) !== String(seniorId))
    throw unsupported();
  if (typeof etag !== "string" || !etag.trim())
    throw new Error(
      "Reload the report before saving: its ETag is unavailable.",
    );
  if (
    !identifier(change?.assessment_id) ||
    !validRevision(change.expected_assessment_revision)
  ) {
    throw new Error("An assessment ID and its expected revision are required.");
  }
  const targets = Array.isArray(document.edit_targets)
    ? document.edit_targets
    : Object.values(document.edit_targets || {});
  const target = targets.find(
    (entry) =>
      object(entry) &&
      String(entry.assessment_id) === String(change.assessment_id),
  );
  const targetRevision =
    target?.assessment_revision ??
    target?.revision ??
    target?.expected_assessment_revision;
  if (
    !target ||
    String(targetRevision) !== String(change.expected_assessment_revision)
  ) {
    throw new Error(
      "Reload the report before saving: this assessment is no longer an available edit target.",
    );
  }
  if (target.editable === false || target.disabled === true)
    throw new Error("This assessment cannot be edited.");
  if (change.fim_overrides !== undefined) {
    const items = target.editable_item_ids ?? target.item_ids ?? [];
    if (
      document.context.mode !== "rehab" ||
      !allowed(document.capabilities.can_edit_fim) ||
      !Array.isArray(change.fim_overrides) ||
      !change.fim_overrides.length ||
      change.fim_overrides.some(
        (entry) =>
          !object(entry) ||
          !identifier(entry.item_id) ||
          (!allowed(target.can_edit_fim) &&
            (!Array.isArray(items) || !items.includes(entry.item_id))) ||
          (entry.fim_override !== null &&
            (!Number.isInteger(entry.fim_override) ||
              entry.fim_override < 1 ||
              entry.fim_override > 7)),
      )
    ) {
      throw new Error(
        "The requested FIM edit is not permitted or contains an invalid value.",
      );
    }
  }
  if (change.remarks !== undefined) {
    const fields = target.editable_fields ?? target.fields ?? [];
    if (
      !allowed(document.capabilities.can_edit_remarks) ||
      !object(change.remarks) ||
      !Object.keys(change.remarks).length ||
      Object.entries(change.remarks).some(
        ([kind, value]) =>
          !["staff", "person"].includes(kind) ||
          typeof value !== "string" ||
          (!allowed(target.can_edit_remarks) &&
            (!Array.isArray(fields) ||
              (!fields.includes("remarks") &&
                !fields.includes(`remarks.${kind}`)))) ||
          value.length >
            (document.remarks?.[kind]?.max_length ??
              document.remarks?.limits?.[kind] ??
              (kind === "staff" ? 200 : 120)),
      )
    ) {
      throw new Error(
        "The requested remark edit is not permitted or contains an invalid value.",
      );
    }
  }
  const body = {
    assessment_id: change.assessment_id,
    expected_assessment_revision: change.expected_assessment_revision,
    ...(change.fim_overrides !== undefined
      ? {
          fim_overrides: change.fim_overrides.map(
            ({ item_id, fim_override }) => ({ item_id, fim_override }),
          ),
        }
      : {}),
    ...(change.remarks !== undefined ? { remarks: change.remarks } : {}),
  };
  if (body.fim_overrides === undefined && body.remarks === undefined)
    throw new Error("No report changes were supplied.");
  const response = await apiClient.patch(
    `${basePath(seniorId)}/${encodeURIComponent(document.report_id)}`,
    body,
    {
      headers: {
        ...headers(),
        "If-Match": etag,
        "Idempotency-Key": idempotencyKey || requestKey(),
      },
      signal,
    },
  );
  const result = reportResult(
    await completedResponse(response, signal),
    seniorId,
  );
  if (
    String(result.document.report_id) !== String(document.report_id) ||
    String(result.document.revision) === String(document.revision)
  )
    throw unsupported();
  return result;
};

export const createMobilityReportShare = async (
  seniorId,
  document,
  { idempotencyKey, signal } = {},
) => {
  unwrapMobilityReport(document);
  if (String(document.context.senior_id) !== String(seniorId))
    throw unsupported();
  if (!allowed(document.capabilities.can_share))
    throw new Error("This report cannot be shared.");
  const response = await apiClient.post(
    "/v6/report",
    {
      kind: "mobility_report",
      seniorId,
      reportId: document.report_id,
      revision: document.revision,
    },
    {
      headers: {
        ...headers(),
        "Idempotency-Key": idempotencyKey || requestKey(),
      },
      signal,
    },
  );
  return response.data?.data ?? response.data;
};

export const getSharedMobilityReport = async (slug, { signal } = {}) => {
  if (!identifier(slug))
    throw new Error("A report share reference is required.");
  const response = await publicApi.get(
    `/v6/report/${encodeURIComponent(slug)}`,
    { headers: headers(), signal },
  );
  const completed = await completedResponse(response, signal, publicApi);
  const result = reportResult(completed, undefined, { publicSnapshot: true });
  const body = completed.data?.data ?? completed.data;
  const expiresAt = body?.snapshot?.expires_at ?? body?.expires_at;
  return {
    ...result,
    ...(expiresAt ? { expiresAt } : {}),
  };
};

export const resolveMobilityShareUrl = (
  share,
  origin = window.location.origin,
) => {
  if (typeof share?.slug === "string" && share.slug.trim()) {
    const base = new URL(origin);
    if (
      !["http:", "https:"].includes(base.protocol) ||
      base.username ||
      base.password
    )
      throw new Error("Invalid report share origin.");
    return new URL(
      `/mobility-report-shared/${encodeURIComponent(share.slug)}`,
      base.origin,
    ).href;
  }
  try {
    const url = new URL(share?.url);
    if (
      ["http:", "https:"].includes(url.protocol) &&
      !url.username &&
      !url.password
    )
      return url.href;
  } catch {
    /* Reject malformed links without exposing response content. */
  }
  throw new Error("The server did not return a valid report share link.");
};
